import React, { useRef, useEffect, useState } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface ImageUploadProps {
  value: File | string | null;
  onChange: (file: File | null) => void;
  onPaste?: (file: File) => void;
  disabled?: boolean;
  maxSizeMB?: number;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  onPaste,
  disabled = false,
  maxSizeMB = 10,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Update preview when value changes
  useEffect(() => {
    if (value instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(value);
    } else if (typeof value === 'string') {
      setPreview(value);
    } else {
      setPreview(null);
    }
  }, [value]);

  // Global paste event listener
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (disabled) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            handleFile(file);
            if (onPaste) {
              onPaste(file);
            }
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [disabled, onPaste]);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return 'Please select an image file';
    }

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      return `Image size must be less than ${maxSizeMB}MB`;
    }

    return null;
  };

  const handleFile = (file: File) => {
    setError(null);
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    onChange(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleRemove = () => {
    onChange(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING[8],
  };

  const hiddenInputStyle: React.CSSProperties = {
    display: 'none',
  };

  const previewContainerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-block',
  };

  const previewImageStyle: React.CSSProperties = {
    maxWidth: '100%',
    height: 'auto',
    maxHeight: '256px',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
  };

  const removeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: SPACING[8],
    right: SPACING[8],
    backgroundColor: COLORS.status.error,
    color: COLORS.bg.panel,
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  };

  const dropZoneStyle: React.CSSProperties = {
    border: `2px dashed ${dragActive ? COLORS.accent.default : COLORS.border.default}`,
    borderRadius: RADIUS,
    padding: SPACING[32],
    textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    backgroundColor: dragActive ? '#EFF6FF' : 'transparent',
    opacity: disabled ? 0.5 : 1,
  };

  const dropZoneContentStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING[8],
  };

  const iconContainerStyle: React.CSSProperties = {
    color: COLORS.text.tertiary,
  };

  const svgStyle: React.CSSProperties = {
    margin: '0 auto',
    height: '48px',
    width: '48px',
    color: COLORS.text.tertiary,
  };

  const uploadTextStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.text.secondary,
  };

  const clickToUploadStyle: React.CSSProperties = {
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.accent.default,
  };

  const helperTextStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.xs,
    color: COLORS.text.tertiary,
  };

  const errorStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.status.error,
  };

  return (
    <div style={containerStyle}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        disabled={disabled}
        style={hiddenInputStyle}
      />

      {preview ? (
        <div style={previewContainerStyle}>
          <img
            src={preview}
            alt="Upload preview"
            style={previewImageStyle}
          />
          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              style={removeButtonStyle}
              title="Remove image"
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <div
          style={dropZoneStyle}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <div style={dropZoneContentStyle}>
            <div style={iconContainerStyle}>
              <svg
                style={svgStyle}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div style={uploadTextStyle}>
              <span style={clickToUploadStyle}>
                Click to upload
              </span>{' '}
              or drag and drop
            </div>
            <div style={helperTextStyle}>
              Or press Ctrl+V to paste from clipboard
            </div>
            <div style={helperTextStyle}>
              PNG, JPG, GIF up to {maxSizeMB}MB
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={errorStyle}>
          {error}
        </div>
      )}
    </div>
  );
};
